import type { ChapterPlan, ChapterPlanSource, StoryBible } from '../types';
import { areChapterPlansBlank } from './roadmapPersistence.service';

export type PremadeRoadmapParseStatus = 'success' | 'partial' | 'failure';

export interface PremadeRoadmapParseResult {
  status: PremadeRoadmapParseStatus;
  plans: ChapterPlan[];
  unmatchedRawBlocks: string[];
  parsedTitles: string[];
  parseFailureReason?: string;
  warnings: string[];
  countMismatch?: boolean;
  suspectEntryIndexes?: number[];
  suspectEntryReasons?: string[];
}

export interface RoadmapParseCountMismatchPayload {
  expectedChapterCount: number;
  parsedChapterCount: number;
  suspectEntryIndexes: number[];
  suspectEntryReasons: string[];
}

export interface PremadeRoadmapDiagnosticPayload {
  rawLength: number;
  parsedChapterCount: number;
  expectedChapterCount?: number;
  parsedTitles: string[];
  sourceBefore?: ChapterPlanSource;
  sourceAfter?: ChapterPlanSource;
  architectInvoked?: boolean;
  parseFailureReason?: string;
}

const CHAPTER_NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  'twenty-one': 21,
  'twenty-two': 22,
  'twenty-three': 23,
  'twenty-four': 24,
  'twenty-five': 25,
  'twenty-six': 26,
  'twenty-seven': 27,
  'twenty-eight': 28,
  'twenty-nine': 29,
  thirty: 30,
};

const CHAPTER_NUMBER_TOKEN =
  '(\\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|twenty-one|twenty-two|twenty-three|twenty-four|twenty-five|twenty-six|twenty-seven|twenty-eight|twenty-nine|thirty)';

function parseChapterNumberToken(token: string, fallback: number): number {
  const lower = token.toLowerCase();
  const parsed = parseInt(lower, 10);
  if (!Number.isNaN(parsed)) return parsed;
  return CHAPTER_NUMBER_WORDS[lower] ?? fallback;
}

export type ChapterHeadingKind = 'chapter_keyword' | 'numbered_title' | 'em_dash_title';

export interface ParsedChapterHeading {
  chapterNumber: number;
  title: string;
  kind: ChapterHeadingKind;
}

export function parseChapterHeadingLine(line: string): ParsedChapterHeading | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const keywordDelimiter = new RegExp(
    `^(?:#{1,6}\\s*)?(?:chapter|ch|segment|part|vol\\.?|volume)\\s*${CHAPTER_NUMBER_TOKEN}\\s*(?:[:.]|[\\-—–])\\s*(.+)$`,
    'i',
  );
  const keywordMatch = trimmed.match(keywordDelimiter);
  if (keywordMatch) {
    return {
      chapterNumber: parseChapterNumberToken(keywordMatch[1], 1),
      title: keywordMatch[2].trim(),
      kind: 'chapter_keyword',
    };
  }

  const keywordEmDash = new RegExp(
    `^(?:#{1,6}\\s*)?(?:chapter|ch)\\s*${CHAPTER_NUMBER_TOKEN}\\s+[-—–]\\s+(.+)$`,
    'i',
  );
  const emDashKeywordMatch = trimmed.match(keywordEmDash);
  if (emDashKeywordMatch) {
    return {
      chapterNumber: parseChapterNumberToken(emDashKeywordMatch[1], 1),
      title: emDashKeywordMatch[2].trim(),
      kind: 'chapter_keyword',
    };
  }

  const numberedTitle = trimmed.match(/^(\d{1,2})\.\s+([A-Z][^\n]{1,120})$/);
  if (numberedTitle) {
    return {
      chapterNumber: parseInt(numberedTitle[1], 10),
      title: numberedTitle[2].trim(),
      kind: 'numbered_title',
    };
  }

  const emDashTitle = trimmed.match(/^[-—–]\s+([A-Z][A-Z0-9'’:\s-]{2,80})$/);
  if (emDashTitle) {
    return {
      chapterNumber: -1,
      title: emDashTitle[1].trim(),
      kind: 'em_dash_title',
    };
  }

  return null;
}

function isSuspectParsedChapterTitle(title: string): string | null {
  const trimmed = title.trim();
  if (!trimmed) return null;
  if (/^[a-z]/.test(trimmed) && trimmed.length > 24) {
    return 'title_starts_lowercase_long_sentence';
  }
  if (/\b(reveals that|without creating|paid them to|force .+ off the land)\b/i.test(trimmed)) {
    return 'narrative_clause_promoted_as_title';
  }
  if (trimmed.split(/\s+/).length >= 12 && /[a-z]/.test(trimmed.charAt(0))) {
    return 'overlong_narrative_line';
  }
  return null;
}

export function detectSuspectRoadmapEntries(plans: ChapterPlan[]): {
  suspectEntryIndexes: number[];
  suspectEntryReasons: string[];
} {
  const suspectEntryIndexes: number[] = [];
  const suspectEntryReasons: string[] = [];
  plans.forEach((plan, index) => {
    const reason = isSuspectParsedChapterTitle(plan.title);
    if (reason) {
      suspectEntryIndexes.push(index);
      suspectEntryReasons.push(reason);
    }
  });
  return { suspectEntryIndexes, suspectEntryReasons };
}

export function logRoadmapParseCountMismatch(payload: RoadmapParseCountMismatchPayload): void {
  console.log(`[ROADMAP PARSE COUNT MISMATCH] ${JSON.stringify(payload)}`);
}

interface ChapterBlock {
  chapterNumber: number;
  headerTitle: string;
  lines: string[];
  headingKind?: ChapterHeadingKind;
}

function blockToPlan(block: ChapterBlock): ChapterPlan | null {
  const rawBlockText = block.lines.join('\n');
  let title = block.headerTitle;
  let pov = 'needs confirmation';
  let location = 'needs confirmation';
  let emotionalShift = 'needs confirmation';
  let endingHook = 'needs confirmation';
  let summary = '';
  const beats: string[] = [];
  const forbiddenMoves: string[] = [];
  const continuityNotes: string[] = [];

  const bodyLines = block.lines.map((line) => line.trim()).filter(Boolean);
  let hasExplicitFields = false;
  for (const line of bodyLines) {
    if (
      /^(?:pov|povCharacter|point of view|pov character|location|setting|place|locationFocus|emotionalShift|emotional shift|shift|endingHook|ending hook|hook|summary|chapterSummary|desc|description|forbiddenMoves|forbidden moves|forbidden|continuityNotes|continuity notes|notes|note|requiredBeats|beats|beat|beat\s*\d+)\s*:/i.test(
        line,
      )
    ) {
      hasExplicitFields = true;
      break;
    }
  }

  if (hasExplicitFields) {
    for (const line of bodyLines) {
      const povMatch = line.match(/^(?:pov|povCharacter|point of view|pov character)\s*:\s*(.*)$/i);
      const locMatch = line.match(/^(?:location|setting|place|locationFocus)\s*:\s*(.*)$/i);
      const emShiftMatch = line.match(/^(?:emotionalShift|emotional shift|shift)\s*:\s*(.*)$/i);
      const hookMatch = line.match(/^(?:endingHook|ending hook|hook)\s*:\s*(.*)$/i);
      const sumMatch = line.match(/^(?:summary|chapterSummary|desc|description)\s*:\s*(.*)$/i);
      const forbiddenMatch = line.match(/^(?:forbiddenMoves|forbidden moves|forbidden)\s*:\s*(.*)$/i);
      const notesMatch = line.match(/^(?:continuityNotes|continuity notes|notes|note)\s*:\s*(.*)$/i);
      const beatMatch = line.match(/^(?:requiredBeats|beats|beat|beat\s*\d+)\s*:\s*(.*)$/i);
      const titleMatch = line.match(/^(?:title|chapterTitle|name)\s*:\s*(.*)$/i);

      if (titleMatch) {
        title = titleMatch[1].trim();
      } else if (povMatch) {
        pov = povMatch[1].trim() || 'needs confirmation';
      } else if (locMatch) {
        location = locMatch[1].trim() || 'needs confirmation';
      } else if (emShiftMatch) {
        emotionalShift = emShiftMatch[1].trim() || 'needs confirmation';
      } else if (hookMatch) {
        endingHook = hookMatch[1].trim() || 'needs confirmation';
      } else if (sumMatch) {
        summary = sumMatch[1].trim();
      } else if (forbiddenMatch) {
        const splitVal = forbiddenMatch[1].split(/[;,•\-]+/).map((s) => s.trim()).filter(Boolean);
        if (splitVal.length > 0) forbiddenMoves.push(...splitVal);
      } else if (notesMatch) {
        const splitVal = notesMatch[1].split(/[;,•\-]+/).map((s) => s.trim()).filter(Boolean);
        if (splitVal.length > 0) continuityNotes.push(...splitVal);
      } else if (beatMatch) {
        const splitVal = beatMatch[1].split(/[;,•\-]+/).map((s) => s.trim()).filter(Boolean);
        if (splitVal.length > 0) beats.push(...splitVal);
      } else if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
        const beatText = line.replace(/^[•\-\*\s]+/, '').trim();
        if (beatText) beats.push(beatText);
      } else if (summary) {
        summary += `\n${line}`;
      } else {
        summary = line;
      }
    }
  } else if (bodyLines.length > 0) {
    let startIndex = 0;
    if (!title) {
      title = bodyLines[0];
      startIndex = 1;
    }
    summary = bodyLines.slice(startIndex).join('\n');
  }

  if (!title) title = `Chapter ${block.chapterNumber}`;
  if (!summary) summary = rawBlockText.trim() || 'needs confirmation';

  const placeholderOnly =
    (!summary || summary === 'needs confirmation') && (!title || title.startsWith('Chapter '));
  if (placeholderOnly && !rawBlockText.trim()) return null;

  const locationFocusArr =
    location && location !== 'needs confirmation'
      ? location.split(',').map((s) => s.trim()).filter(Boolean)
      : ['needs confirmation'];

  return {
    id: `plan_premade_${block.chapterNumber}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    chapterNumber: block.chapterNumber,
    title,
    pov,
    locationFocus: locationFocusArr,
    emotionalShift,
    endingHook,
    summary,
    requiredBeats: beats,
    forbiddenMoves,
    continuityNotes,
    rawText: rawBlockText,
    status: 'planned',
    updatedAt: new Date().toISOString(),
    source: 'USER_ROADMAP',
    chapterTitle: title,
    chapterSummary: summary,
    povCharacter: pov,
    location,
    beats,
    hook: endingHook,
    notes: continuityNotes,
  } as ChapterPlan;
}

export function parsePremadeRoadmap(rawText: string, expectedChapterCount?: number): PremadeRoadmapParseResult {
  const text = rawText?.trim() || '';
  if (!text) {
    return {
      status: 'failure',
      plans: [],
      unmatchedRawBlocks: [],
      parsedTitles: [],
      parseFailureReason: 'No premade roadmap text was provided.',
      warnings: [],
    };
  }

  const lines = text.split('\n');
  const blocks: ChapterBlock[] = [];
  let chNumCounter = 1;
  let currentBlock: ChapterBlock | null = null;

  for (const rawLine of lines) {
    const trimmedLine = rawLine.trim();
    const isKeyOrBeat =
      /^(?:pov|povCharacter|point of view|pov character|location|setting|place|locationFocus|emotionalShift|emotional shift|shift|endingHook|ending hook|hook|summary|chapterSummary|desc|description|forbiddenMoves|forbidden moves|forbidden|continuityNotes|continuity notes|notes|note|requiredBeats|beats|beat|beat\s*\d+)\s*:/i.test(
        trimmedLine,
      );
    const heading = !isKeyOrBeat ? parseChapterHeadingLine(trimmedLine) : null;

    if (heading) {
      const chNum = heading.chapterNumber > 0 ? heading.chapterNumber : chNumCounter;
      chNumCounter = chNum + 1;
      currentBlock = {
        chapterNumber: chNum,
        headerTitle: heading.title,
        lines: [],
        headingKind: heading.kind,
      };
      blocks.push(currentBlock);
    } else {
      if (!currentBlock && trimmedLine) {
        currentBlock = { chapterNumber: 1, headerTitle: '', lines: [] };
        blocks.push(currentBlock);
      }
      if (currentBlock) currentBlock.lines.push(rawLine);
    }
  }

  const plans: ChapterPlan[] = [];
  const unmatchedRawBlocks: string[] = [];
  for (const block of blocks) {
    const plan = blockToPlan(block);
    if (plan) plans.push(plan);
    else unmatchedRawBlocks.push(block.lines.join('\n'));
  }

  plans.sort((a, b) => a.chapterNumber - b.chapterNumber);

  const emptyOrPlaceholderOnly =
    plans.length > 0 &&
    plans.every(
      (plan) =>
        (!plan.summary || plan.summary === 'needs confirmation') &&
        (!plan.title || plan.title.startsWith('Chapter ')),
    );

  if (!plans.length || emptyOrPlaceholderOnly) {
    return {
      status: 'failure',
      plans: [],
      unmatchedRawBlocks: blocks.map((block) => block.lines.join('\n')),
      parsedTitles: [],
      parseFailureReason: 'Roadmap detected, but could not be parsed into chapter cards.',
      warnings: [],
    };
  }

  const warnings: string[] = [];
  if (unmatchedRawBlocks.length) {
    warnings.push(`${unmatchedRawBlocks.length} roadmap block(s) require manual review.`);
  }

  const headingKinds = new Set(blocks.map((block) => block.headingKind).filter(Boolean));
  if (headingKinds.size > 1) {
    warnings.push('Mixed chapter heading formats detected. Review parsed chapter cards before approval.');
  }

  const { suspectEntryIndexes, suspectEntryReasons } = detectSuspectRoadmapEntries(plans);
  const countMismatch = Boolean(expectedChapterCount && plans.length !== expectedChapterCount);
  if (countMismatch && expectedChapterCount) {
    warnings.push(
      `Parsed ${plans.length} chapters but target count is ${expectedChapterCount}. Approval is blocked until the mismatch is resolved.`,
    );
    logRoadmapParseCountMismatch({
      expectedChapterCount,
      parsedChapterCount: plans.length,
      suspectEntryIndexes,
      suspectEntryReasons,
    });
  } else if (suspectEntryIndexes.length) {
    warnings.push(
      `Suspect parsed chapter title(s) at index ${suspectEntryIndexes.map((index) => index + 1).join(', ')} require review.`,
    );
  }

  return {
    status: unmatchedRawBlocks.length || warnings.length ? 'partial' : 'success',
    plans,
    unmatchedRawBlocks,
    parsedTitles: plans.map((plan) => plan.title),
    warnings,
    ...(countMismatch ? { countMismatch: true, suspectEntryIndexes, suspectEntryReasons } : {}),
    ...(!countMismatch && suspectEntryIndexes.length ? { suspectEntryIndexes, suspectEntryReasons } : {}),
  };
}

export function shouldPreferPremadeRoadmap(storyBible: StoryBible): boolean {
  return Boolean(storyBible.premadeRoadmap?.trim() || storyBible.originalUserRoadmapRaw?.trim());
}

export function shouldAutoApplyPremadeRoadmap(storyBible: StoryBible): boolean {
  if (!shouldPreferPremadeRoadmap(storyBible)) return false;
  if (storyBible.activeRoadmapSource === 'USER_ROADMAP' && !areChapterPlansBlank(storyBible.chapterPlans)) {
    return false;
  }
  if (
    storyBible.activeRoadmapSource &&
    ['AI_GENERATED', 'AI_REGENERATED', 'AI_ROADMAP'].includes(storyBible.activeRoadmapSource)
  ) {
    return false;
  }
  return areChapterPlansBlank(storyBible.chapterPlans);
}

export function applyPremadeRoadmapParse(
  storyBible: StoryBible,
  parseResult: PremadeRoadmapParseResult,
): StoryBible {
  if (parseResult.status === 'failure') {
    return {
      ...storyBible,
      chapterPlans: [],
      roadmapParseWarnings: parseResult.warnings,
      activeRoadmapSource: undefined,
    };
  }

  return {
    ...storyBible,
    chapterPlans: parseResult.plans,
    targetChapterCount: storyBible.targetChapterCount,
    activeRoadmapSource: 'USER_ROADMAP',
    originalUserRoadmapRaw: storyBible.originalUserRoadmapRaw || storyBible.premadeRoadmap,
    originalUserRoadmapParsed: parseResult.plans,
    roadmapParseWarnings: parseResult.warnings,
    isRoadmapApproved: false,
  };
}

export function logPremadeRoadmapDiagnostic(
  tag:
    | '[PREMADE ROADMAP INPUT]'
    | '[PREMADE ROADMAP PARSE]'
    | '[PREMADE ROADMAP STATE APPLY]'
    | '[ROADMAP SOURCE CHANGE]'
    | '[ROADMAP APPROVAL PERSIST]',
  payload: PremadeRoadmapDiagnosticPayload,
): void {
  console.log(`${tag} ${JSON.stringify(payload)}`);
}

export function sourceForArchitectInvocation(
  currentSource?: ChapterPlanSource,
  isRegenerate = false,
): ChapterPlanSource {
  if (isRegenerate) return 'AI_REGENERATED';
  if (currentSource === 'AI_GENERATED' || currentSource === 'AI_REGENERATED' || currentSource === 'AI_ROADMAP') {
    return 'AI_REGENERATED';
  }
  return 'AI_GENERATED';
}

export function tagPlansWithSource(plans: ChapterPlan[], source: ChapterPlanSource): ChapterPlan[] {
  return plans.map((plan) => ({ ...plan, source }));
}

export function serializeRoadmapForReload(storyBible: StoryBible): {
  activeRoadmapSource?: ChapterPlanSource;
  originalUserRoadmapRaw?: string;
  originalUserRoadmapParsed?: ChapterPlan[];
} {
  return {
    activeRoadmapSource: storyBible.activeRoadmapSource,
    originalUserRoadmapRaw: storyBible.originalUserRoadmapRaw,
    originalUserRoadmapParsed: storyBible.originalUserRoadmapParsed,
  };
}
